<script lang="ts">
  import Alert from '@leaflink/stash/Alert.vue';
  import useConfirmBeforeClosing from '@leaflink/stash/useConfirmBeforeClosing';
  import { storeToRefs } from 'pinia';
  import { computed, inject, onMounted, provide, reactive, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import FeatureEnrollmentLayout from '@/components/FeatureEnrollment/FeatureEnrollmentLayout.vue';
  import { SPLIT_NAMES } from '@/constants';
  import { INVENTORY_INSIGHTS_SUCCESS_MODAL_STORAGE_KEY } from '@/constants/storage';
  import { BillingPaymentMethod } from '@/models/billing';
  import {
    DraftOrdersPostResponse,
    FeatureEnrollmentEmitterArgs,
    FeatureEnrollmentStep,
  } from '@/models/featureEnrollment';
  import { InventoryInsightsProviderProps, PosCredentials, PosSystemName } from '@/models/inventoryInsights';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';
  import { trackWithSegment } from '@/segment';
  import { CompanyBillingRequestData } from '@/services/api/models/settings';

  import BillingSetup from './steps/BillingSetup.vue';
  import ConnectYourPOS from './steps/ConnectYourPOS.vue';
  import ReviewInformation from './steps/ReviewInformation.vue';
  import WhatYoullNeed from './steps/WhatYoullNeed.vue';

  export const LEAFLINK_PLUS_PRODUCT_NAME = 'LeafLink Plus';
  const INVENTORY_INSIGHTS_INJECTION_KEY = 'inventory-insights';

  const preventNativeDialog = ref(false);
  const order = ref<DraftOrdersPostResponse | null>(null);

  export const useInventoryInsightsProvider = () => {
    const injectionData = inject<InventoryInsightsProviderProps>(INVENTORY_INSIGHTS_INJECTION_KEY);

    if (!injectionData) {
      throw new Error(`This composable must be used within <InventoryInsights>.`);
    }

    return injectionData;
  };
</script>

<script setup lang="ts">
  const router = useRouter();
  const route = useRoute();

  const { userActiveCompany, userActiveCompanyPermissions, getIsSplitEnabled } = storeToRefs(useUserStore());

  // Bypass confirmation if dev mode is on to prevent friction with hot-reload
  if (import.meta.env.MODE !== 'development') {
    useConfirmBeforeClosing(() => !preventNativeDialog.value);
  }

  const steps: FeatureEnrollmentStep[] = [
    {
      text: i18n.t('inventoryInsights.steps.whatYoullNeed'),
      illustration: 'easy-reconciliations',
      component: WhatYoullNeed,
    },
    {
      text: i18n.t('inventoryInsights.steps.connectYourPOS'),
      illustration: 'microchip',
      component: ConnectYourPOS,
    },
    {
      text: i18n.t('inventoryInsights.steps.billingSetup'),
      illustration: 'payment-processing',
      component: BillingSetup,
    },
    {
      text: i18n.t('inventoryInsights.steps.reviewInformation'),
      illustration: 'discover',
      component: ReviewInformation,
    },
  ];

  const enrollmentPrice = ref(299);
  const selectedPosSystem = ref<PosSystemName>('dutchie');
  const posCredentials = reactive<PosCredentials>({
    dutchie: {
      apiKey: '',
    },
    flowhub: {
      apiKey: '',
      clientId: '',
      locationId: '',
    },
  });

  // clear pos credentials when switching pos system - prevents stale data from creating false positives in validation
  // located here because the change can happen from step 1 and 2
  watch(selectedPosSystem, (newValue, oldValue) => {
    if (newValue !== oldValue) {
      posCredentials.dutchie.apiKey = '';
      posCredentials.flowhub.apiKey = '';
      posCredentials.flowhub.clientId = '';
      posCredentials.flowhub.locationId = '';
    }
  });

  const billingRequestArgs = reactive<CompanyBillingRequestData>({
    billingEmail: '',
    company: null,
    id: null,
  });

  const company = reactive<InventoryInsightsProviderProps['company']>({
    id: userActiveCompany.value?.id || null,
    name: userActiveCompany.value?.name || '',
    address: userActiveCompany.value?.corporateAddress || null,
    license: userActiveCompany.value?.licenses[0] || null,
    slug: userActiveCompany.value?.slug || '',
  });

  const addPosStatus = reactive<InventoryInsightsProviderProps['addPosStatus']>({
    isLoading: false,
    isValid: false,
    validationError: {
      dutchie: {
        apiKey: '',
      },
      flowhub: {
        apiKey: '',
        clientId: '',
        locationId: '',
      },
    },
  });

  const isAdmin = computed(() => Boolean(userActiveCompanyPermissions.value?.staffIsAdmin));
  const isPosApiEnabled = computed(() => Boolean(userActiveCompany.value?.enableRetailerCategoryManagement));
  const isNueIntegrationEnabled = computed(() => {
    //TODO: remove after nue integration is complete
    return getIsSplitEnabled.value(SPLIT_NAMES.ENROLLMENT_NUE_INTEGRATION);
  });

  const whichExitButtonWasEngaged = ref('');
  const paymentMethods = ref<BillingPaymentMethod[]>([]);

  const onNext = ({ currentStep, nextStep }: FeatureEnrollmentEmitterArgs) => {
    trackWithSegment({
      event: 'inventoryInsightsEnrollmentNavigationClicked',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: LEAFLINK_PLUS_PRODUCT_NAME,
        element_engaged: 'Next Button',
        page_engaged_from: currentStep?.text,
        page_navigated_to: nextStep?.text,
      },
    });
  };

  const onBack = ({ event, currentStep, previousStep }: FeatureEnrollmentEmitterArgs) => {
    const target = event?.currentTarget ? (event.currentTarget as HTMLElement) : null;
    const isEditButton = target?.title === i18n.t('common.editPaymentMethod');

    let elementEngaged = 'Back Button';

    if (isEditButton) {
      elementEngaged = 'Edit Icon';
    }

    trackWithSegment({
      event: 'inventoryInsightsEnrollmentNavigationClicked',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: LEAFLINK_PLUS_PRODUCT_NAME,
        element_engaged: elementEngaged,
        page_engaged_from: currentStep?.text,
        page_navigated_to: previousStep?.text,
      },
    });
  };

  const onStepClick = ({ event, currentStep }: FeatureEnrollmentEmitterArgs) => {
    const target = event?.target as HTMLElement;
    const targetStepText = target.textContent?.trim();

    trackWithSegment({
      event: 'inventoryInsightsEnrollmentNavigationClicked',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: LEAFLINK_PLUS_PRODUCT_NAME,
        element_engaged: 'Sidebar Navigation',
        page_engaged_from: currentStep?.text,
        page_navigated_to: targetStepText,
      },
    });
  };

  const onStepChange = (args: FeatureEnrollmentEmitterArgs) => {
    trackWithSegment({
      event: 'inventoryInsightsEnrollmentStepViewed',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: LEAFLINK_PLUS_PRODUCT_NAME,
        page_viewed: args.currentStep?.text,
      },
    });
  };

  const onOpenExitDialog = ({ event }: FeatureEnrollmentEmitterArgs) => {
    const targetDataTestAttrValue = (event?.currentTarget as HTMLButtonElement).getAttribute('data-test');

    if (targetDataTestAttrValue?.startsWith('header')) {
      whichExitButtonWasEngaged.value = 'X to close out';
      return;
    }

    whichExitButtonWasEngaged.value = 'Exit Enrollment button';
  };

  const onExit = ({ currentStep }: FeatureEnrollmentEmitterArgs) => {
    preventNativeDialog.value = true;

    trackWithSegment({
      event: 'inventoryInsightsEnrollmentExited',
      properties: {
        product_name: LEAFLINK_PLUS_PRODUCT_NAME,
        element_engaged: whichExitButtonWasEngaged.value,
        page_engaged_from: currentStep?.text,
      },
    });

    if (route.query.previous && typeof route.query.previous === 'string') {
      router.push({ name: route.query.previous });
    } else {
      router.push(`/c/${userActiveCompany.value?.slug}`);
    }
  };

  provide<InventoryInsightsProviderProps>(INVENTORY_INSIGHTS_INJECTION_KEY, {
    paymentMethods,
    billingRequestArgs,
    company,
    addPosStatus,
    selectedPosSystem,
    posCredentials,
    enrollmentPrice,
    productName: LEAFLINK_PLUS_PRODUCT_NAME,
    isAdmin,
    isPosApiEnabled,
    preventNativeDialog,
    order,
    isNueIntegrationEnabled, //TODO: remove after nue integration is complete
  });

  onMounted(() => {
    window.sessionStorage.removeItem(INVENTORY_INSIGHTS_SUCCESS_MODAL_STORAGE_KEY);
  });
</script>

<template>
  <FeatureEnrollmentLayout
    :feature-name="i18n.t('inventoryInsights.activateLeafLinkPlus')"
    :steps="steps"
    @on-exit="onExit"
    @on-next="onNext"
    @on-back="onBack"
    @on-step-click="onStepClick"
    @on-step-change="onStepChange"
    @on-open-exit-dialog="onOpenExitDialog"
  >
    <template v-if="!isAdmin" #alert>
      <Alert severity="warning" permanent variant="filled" radius="none" centered>
        {{ i18n.t('inventoryInsights.alertsLeafLinkPlus.nonAdminAlert') }}
      </Alert>
    </template>

    <template v-else-if="isPosApiEnabled" #alert>
      <Alert severity="warning" permanent variant="filled" radius="none" centered>
        {{ i18n.t('inventoryInsights.alertsLeafLinkPlus.alreadyEnrolled') }}
      </Alert>
    </template>
  </FeatureEnrollmentLayout>
</template>
